import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || '';

export interface ParkDto {
  name: string;
  id: string;
  source: string; // 'API' | 'MANUAL'
}

export function useParks() {
  return useQuery({
    queryKey: ['admin-parks'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/api/admin/parks`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      return response.json() as Promise<ParkDto[]>;
    },
  });
}

interface CreateParkInput {
  name: string;
  externalId?: string;
}

export function useCreatePark() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, externalId }: CreateParkInput) => {
      const response = await fetch(`${API_BASE}/api/admin/parks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, externalId }),
      });
      if (!response.ok) throw new Error(`Failed to create park: ${response.status}`);
      return response.json() as Promise<ParkDto>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-parks'] });
    },
  });
}

export function useUpdatePark() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const response = await fetch(`${API_BASE}/api/admin/parks/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error(`Failed to update park: ${response.status}`);
      return response.json() as Promise<ParkDto>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-parks'] });
    },
  });
}

export function useDeletePark() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_BASE}/api/admin/parks/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error(`Failed to delete park: ${response.status}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-parks'] });
    },
  });
}
